import {
    Editor,
    Element as SlateElement,
    Path,
    Point,
    Range,
    Text,
    Transforms,
} from "slate";

export interface SlashTrigger {
    /** The text typed after `/`, up to the first space. */
    command: string;
    /** The text typed after `/command `. `null` until the space is typed. */
    query: string | null;
}

interface ParsedSlashTrigger extends SlashTrigger {
    startOffset: number;
}

const SLASH_TRIGGER = /(^|\s)\/(\w*)(?: ([^/\n]*))?$/;
const ENDS_WITH_NON_WHITESPACE = /\S$/;

export function parseSlashTrigger({
    textBeforeCursor,
    textAfterCursor,
}: {
    textBeforeCursor: string;
    textAfterCursor: string;
}): ParsedSlashTrigger | null {
    if (!/^(\s|$)/.test(textAfterCursor)) {
        return null;
    }
    const match = SLASH_TRIGGER.exec(textBeforeCursor);
    if (!match) {
        return null;
    }
    return {
        startOffset: match.index + match[1].length,
        command: match[2],
        query: match[3] ?? null,
    };
}

function pointAtBlockOffset({
    editor,
    blockPath,
    offset,
}: {
    editor: Editor;
    blockPath: Path;
    offset: number;
}): Point | null {
    let remaining = offset;
    for (const [node, path] of Editor.nodes(editor, {
        at: blockPath,
        match: Text.isText,
    })) {
        if (remaining < node.text.length) {
            return { path, offset: remaining };
        }
        remaining -= node.text.length;
    }
    return null;
}

/**
 * Types "/" at the caret so the slash menu opens, with a leading space when
 * the caret follows a word (the trigger needs whitespace or a line start).
 */
export function insertSlashTrigger(editor: Editor) {
    const { selection } = editor;
    if (!selection) {
        return;
    }
    const start = Range.start(selection);
    const charBefore = Editor.before(editor, start, { unit: "character" });
    const textBefore = charBefore
        ? Editor.string(editor, { anchor: charBefore, focus: start })
        : "";
    Transforms.insertText(
        editor,
        ENDS_WITH_NON_WHITESPACE.test(textBefore) ? " /" : "/",
    );
}

export function getSlashTrigger(
    editor: Editor,
): (SlashTrigger & { range: Range }) | null {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
        return null;
    }
    const point = selection.anchor;
    const blockEntry = Editor.above(editor, {
        at: point,
        match: (node) =>
            SlateElement.isElement(node) && Editor.isBlock(editor, node),
    });
    if (!blockEntry) {
        return null;
    }
    const [, blockPath] = blockEntry;
    const parsed = parseSlashTrigger({
        textBeforeCursor: Editor.string(editor, {
            anchor: Editor.start(editor, blockPath),
            focus: point,
        }),
        textAfterCursor: Editor.string(editor, {
            anchor: point,
            focus: Editor.end(editor, blockPath),
        }),
    });
    if (!parsed) {
        return null;
    }
    const anchor = pointAtBlockOffset({
        editor,
        blockPath,
        offset: parsed.startOffset,
    });
    if (!anchor) {
        return null;
    }
    return {
        command: parsed.command,
        query: parsed.query,
        range: { anchor, focus: point },
    };
}
